/**
 * Theme control. A single icon-only trigger that reports the *resolved* theme,
 * opening a three-way choice — following the OS is a first-class option, not a
 * hidden preference.
 */

import { useId } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';

import { IconButton, Menu, type MenuIcon, type MenuItem } from '@/components/ui';
import { THEME_OPTIONS, useTheme, type ThemeMode } from '@/lib/theme';

const MODE_ICON: Record<ThemeMode, MenuIcon> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const MODE_HINT: Record<ThemeMode, string> = {
  light: 'always light',
  dark: 'always dark',
  system: 'follows the system',
};

export interface ThemeToggleProps {
  size?: 'sm' | 'md';
}

export function ThemeToggle({ size = 'md' }: ThemeToggleProps) {
  const { mode, theme, setMode } = useTheme();
  const descriptionId = useId();
  const description = `Currently ${MODE_HINT[mode]}`;
  const Resolved = theme === 'light' ? Sun : Moon;
  /** The one `⌘⇧L` will land on, so the hint is never a lie. */
  const flipTarget: ThemeMode = theme === 'dark' ? 'light' : 'dark';

  const items: MenuItem[] = [
    { id: 'theme-label', type: 'label', label: 'Appearance' },
    ...THEME_OPTIONS.map<MenuItem>((option) => ({
      id: `theme-${option.value}`,
      type: 'radio',
      label: option.label,
      icon: MODE_ICON[option.value],
      checked: mode === option.value,
      onSelect: () => setMode(option.value),
      shortcut: option.value === flipTarget ? ['mod', 'shift', 'l'] : undefined,
    })),
  ];

  return (
    <>
      <Menu
        items={items}
        align="end"
        label="Appearance"
        width={188}
        trigger={
          <IconButton
            size={size}
            // The name is the ACTION, and it is the same string in all three
            // modes. `Appearance — follows the system` announced as "Appearance
            // — follows the system, button": the reader was told the current
            // value and never told what pressing it does, and the control
            // changed identity on every press.
            label="Switch appearance"
            // The VALUE then needs a channel of its own on each side, because
            // it is the one thing the icon cannot draw — the moon says "dark",
            // not "dark because you pinned it" or "dark because the OS is".
            //
            // Sighted: the tooltip. It used to be a verbatim repeat of the
            // accessible name, so hovering a 28×28 glyph returned exactly what
            // the glyph had already said and the mode stayed unreachable
            // without opening the menu.
            //
            // Assistive: `aria-describedby` at a visually-hidden span, with
            // `aria-description` kept beside it. Riding on `aria-description`
            // alone put the value on a property Firefox has never implemented,
            // so Firefox + NVDA announced "Switch appearance, menu pop-up
            // button, collapsed" and the mode was unreachable there too. Where
            // both are supported the description is computed from
            // `aria-describedby` first, so the string is announced once.
            tooltipContent={`Appearance — ${MODE_HINT[mode]}`}
            aria-description={description}
            aria-describedby={descriptionId}
            tooltipSide="bottom"
          >
            <Resolved />
          </IconButton>
        }
      />
      {/* `sr-only` is out of flow, so it is not a flex item and adds no gap to
          whatever row the toggle is mounted in. */}
      <span id={descriptionId} className="sr-only">
        {description}
      </span>
    </>
  );
}

/** Shared by the command palette and the user menu, so the wording never drifts. */
export function themeCommandLabel(theme: 'dark' | 'light'): string {
  return theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
}
